import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Box, Collapse, IconButton, Tooltip, Typography, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useIntl } from 'react-intl';
import { ReactComponent as CheckBadge } from '../../assets/images/icons/check.svg';
import { palette } from '../../settings/themes/colors';
import { SEPARATED_DATE } from '../../utils/utils';
import { ConfirmationModal } from './ConfirmationModal';
import { MarkReachedDateModal } from './MarkReachedDateModal';

const useStyles = makeStyles(theme => ({
  root: {
    border: `1px solid ${palette.darkGray}33`,
    background: palette.platinum,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    gap: theme.spacing(1)
  },
  name: {
    flex: 1,
    '& a': {
      color: palette.cerulean,
      textDecoration: 'underline'
    }
  },
  iconButton: {
    padding: theme.spacing(0.5)
  },
  checkIcon: {
    width: 18,
    height: 18,
    color: palette.black,
    display: 'block'
  },
  checkIconReached: {
    opacity: 1
  },
  checkIconUnreached: {
    opacity: 0.35
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    borderTop: `1px solid ${palette.darkGray}22`,
    gap: theme.spacing(2),
    color: palette.dimGray,
    fontSize: 13
  },
  detailLabel: {
    minWidth: 90,
    fontWeight: 500
  },
  detailDate: {
    flex: 1
  },
  removeLink: {
    color: palette.cerulean,
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 13,
    lineHeight: 1.5,
    letterSpacing: '0.00938em'
  },
  markFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    borderTop: `1px solid ${palette.darkGray}22`,
    fontSize: 13,
    color: palette.dimGray
  },
  markLink: {
    color: palette.cerulean,
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 13,
    lineHeight: 1.5,
    letterSpacing: '0.00938em'
  },
  confirmSubtitle: {
    color: palette.dimGray
  }
}));

const formatDate = value => (value ? moment(value).format(SEPARATED_DATE) : '');

export const ProjectAssociationItem = ({ project, onUnassign, onMarkReached, onUnmarkReached }) => {
  const classes = useStyles();
  const { messages: intlMessages } = useIntl();
  const { id, name, associatedAt, trackReached, reached, reachedAt } = project;

  const [expanded, setExpanded] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // 'unassign' | 'unmark'
  const [dateModalOpen, setDateModalOpen] = useState(false);

  const showReachedControls = trackReached === true;
  const isReached = showReachedControls && reached === true;

  const toggleExpand = () => setExpanded(prev => !prev);

  const handleConfirm = () => {
    if (confirmType === 'unassign') {
      onUnassign(id);
    } else if (confirmType === 'unmark') {
      onUnmarkReached(id);
    }
    setConfirmType(null);
  };

  const handleMarkConfirm = reachedAtValue => {
    onMarkReached(id, reachedAtValue);
    setDateModalOpen(false);
  };

  const confirmTitle =
    confirmType === 'unmark'
      ? intlMessages['beneficiaries.reached.confirmUnmarkTitle']
      : intlMessages['beneficiaries.reached.confirmUnassignTitle'];
  const confirmSubtitle =
    confirmType === 'unmark'
      ? intlMessages['beneficiaries.reached.confirmUnmarkSubtitle']
      : intlMessages['beneficiaries.reached.confirmUnassignSubtitle'];

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.name}>
          <Link to={`/projects/${id}`}>{name}</Link>
        </Typography>
        {showReachedControls && (
          <Tooltip title={intlMessages['beneficiaries.reached.label']}>
            <span className={isReached ? classes.checkIconReached : classes.checkIconUnreached} style={{ display: 'inline-flex' }}>
              <CheckBadge className={classes.checkIcon} />
            </span>
          </Tooltip>
        )}
        <IconButton aria-label="expand" className={classes.iconButton} onClick={toggleExpand}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {isReached && (
          <Box className={classes.detailRow}>
            <Typography className={classes.detailLabel}>{intlMessages['beneficiaries.reached.label']}</Typography>
            <Typography className={classes.detailDate}>{formatDate(reachedAt)}</Typography>
            <button type="button" className={classes.removeLink} onClick={() => setConfirmType('unmark')}>
              {intlMessages['beneficiaries.reached.remove']}
            </button>
          </Box>
        )}
        <Box className={classes.detailRow}>
          <Typography className={classes.detailLabel}>{intlMessages['beneficiaries.reached.associatedLabel']}</Typography>
          <Typography className={classes.detailDate}>{associatedAt ? formatDate(associatedAt) : '—'}</Typography>
          <button type="button" className={classes.removeLink} onClick={() => setConfirmType('unassign')}>
            {intlMessages['beneficiaries.reached.remove']}
          </button>
        </Box>
        {showReachedControls && !isReached && (
          <Box className={classes.markFooter}>
            <button type="button" className={classes.markLink} onClick={() => setDateModalOpen(true)}>
              {intlMessages['beneficiaries.reached.markAsReached']}
            </button>
          </Box>
        )}
      </Collapse>

      <ConfirmationModal
        open={confirmType !== null}
        onClose={() => setConfirmType(null)}
        onCancel={() => setConfirmType(null)}
        onConfirm={handleConfirm}
        title={<span style={{ fontWeight: 400 }}>{confirmTitle}</span>}
        confirmText={intlMessages['common.yesContinue']}
      >
        <Typography className={classes.confirmSubtitle}>{confirmSubtitle}</Typography>
      </ConfirmationModal>

      <MarkReachedDateModal open={dateModalOpen} onClose={() => setDateModalOpen(false)} onConfirm={handleMarkConfirm} />
    </Box>
  );
};

ProjectAssociationItem.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string,
    associatedAt: PropTypes.string,
    trackReached: PropTypes.bool,
    reached: PropTypes.bool,
    reachedAt: PropTypes.string
  }).isRequired,
  onUnassign: PropTypes.func.isRequired,
  onMarkReached: PropTypes.func.isRequired,
  onUnmarkReached: PropTypes.func.isRequired
};
